import { useEffect, useState } from "react";
import { FieldValues, Path, PathValue } from "react-hook-form";
import { shallowEqual, useSelector } from "react-redux";

import { useThunkDispatch } from "@/redux/dispatcher";
import { load as loadBillingCountries } from "@/redux/modules/api/data/countries/billing/billingCountries.module";
import * as BillingCountriesSelectors from "@/redux/modules/api/data/countries/billing/billingCountries.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

import { FormValuesExploded } from "../formField.types";
import {
    BaseOption,
    LabelType,
    SelectField
} from "../SelectField/selectField.component";

type CountryFieldProperties = {
    placeholder?: string;
    onChange: (value: string | null | undefined) => void;
    disabled?: boolean;
};

type CountryFieldProps<T extends FieldValues> = FormValuesExploded<T> &
    CountryFieldProperties;

const CountryField = <T extends FieldValues>({
    name,
    label,
    placeholder,
    microCopy,
    onChange,
    required,
    disabled
}: CountryFieldProps<T>) => {
    const { t } = useGranite();
    const dispatch = useThunkDispatch();

    const isLoadingBillingCountries = useSelector(
        BillingCountriesSelectors.isLoading
    );
    const billingCountries = useSelector(
        BillingCountriesSelectors.getBillingCountries,
        shallowEqual
    );

    const [hasLoaded, setHasLoaded] = useState(false);

    useEffect(() => {
        if (hasLoaded) return;

        dispatch(loadBillingCountries());
        setHasLoaded(true);
    }, [dispatch, hasLoaded]);

    if (
        billingCountries.length === 0 &&
        !isLoadingBillingCountries &&
        hasLoaded
    ) {
        return null;
    }

    const options: Array<BaseOption<PathValue<T, Path<T>>>> =
        billingCountries.map((country) => {
            return {
                label: country.name,
                value: country.isoCode as PathValue<T, Path<T>>
            };
        });

    if (options.length > 0) {
        options.splice(5, 0, {
            label: LabelType.DIVIDER,
            isDisabled: true
        });
    }

    return (
        <SelectField<T, BaseOption<PathValue<T, Path<T>>>>
            name={name}
            label={label}
            placeholder={placeholder}
            autoComplete={"country"}
            microCopy={microCopy}
            onChange={onChange}
            required={required}
            optionList={options}
            aria-label={t("accessibility.countryField.label")}
            disabled={disabled}
        />
    );
};

export { CountryField };
